import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import MainLayout from '../layout/MainLayout';
import { Bell, Check, CheckCheck, Trash2, ArrowRight } from 'lucide-react';
import { formatRelativeTime } from '../../utils/formatters';

interface Notif {
  id: number;
  title: string;
  message: string;
  is_read: boolean;
  action_url?: string;
  created_at: string;
}

const NAVY = '#12395b';

const NotificationsPage: React.FC = () => {
  const [items, setItems] = useState<Notif[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'unread' ? { unread: 'true' } : {};
      const res = await apiService.notifications.getAll(params);
      setItems(res.data.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markRead = async (id: number) => {
    try { await apiService.notifications.markAsRead(id); setItems(l => l.map(n => n.id === id ? { ...n, is_read: true } : n)); } catch {}
  };
  const markAll = async () => {
    try { await apiService.notifications.markAllAsRead(); setItems(l => l.map(n => ({ ...n, is_read: true }))); } catch {}
  };
  const remove = async (id: number) => {
    try { await apiService.notifications.delete(id); setItems(l => l.filter(n => n.id !== id)); } catch {}
  };
  const open = (n: Notif) => {
    if (!n.is_read) markRead(n.id);
    if (n.action_url) window.location.href = n.action_url;
  };

  const unread = items.filter(n => !n.is_read).length;

  return (
    <MainLayout>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '2rem 1.25rem', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: '#8a8f98' }}>Programa</div>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: NAVY, margin: '4px 0 0' }}>Notificaciones</h1>
          </div>
          {unread > 0 && (
            <button onClick={markAll} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #d9d7d1', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: NAVY, cursor: 'pointer', fontFamily: 'inherit' }}>
              <CheckCheck size={15} /> Marcar todas como leídas
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {(['all', 'unread'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', borderRadius: 999, padding: '6px 14px',
              border: `1px solid ${filter === f ? NAVY : '#d9d7d1'}`, background: filter === f ? NAVY : '#fff',
              color: filter === f ? '#fff' : '#101010',
            }}>{f === 'all' ? 'Todas' : `Sin leer${unread ? ` (${unread})` : ''}`}</button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#8a8f98' }}>Cargando…</p>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#9aa0a8' }}>
            <Bell size={40} style={{ opacity: 0.4 }} />
            <p style={{ marginTop: 12 }}>No tienes notificaciones{filter === 'unread' ? ' sin leer' : ''}.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map(n => (
              <div key={n.id} style={{
                border: '1px solid #eceae4', borderLeft: `3px solid ${n.is_read ? '#eceae4' : '#C8102E'}`,
                borderRadius: 10, padding: '14px 16px', background: n.is_read ? '#fff' : '#fdf7f8',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, cursor: n.action_url ? 'pointer' : 'default' }} onClick={() => open(n)}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 4 }}>{n.title}</div>
                    <div style={{ fontSize: 13.5, color: '#4a4f57', lineHeight: 1.5 }}>{n.message}</div>
                    <div style={{ fontSize: 11.5, color: '#9aa0a8', marginTop: 8 }}>{formatRelativeTime(n.created_at)}</div>
                    {n.action_url && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, fontSize: 12.5, fontWeight: 600, color: '#C8102E' }}>
                        Ir a la plataforma <ArrowRight size={13} />
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                    {!n.is_read && (
                      <button title="Marcar como leída" onClick={() => markRead(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9aa0a8', padding: 2 }}><Check size={16} /></button>
                    )}
                    <button title="Eliminar" onClick={() => remove(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c9b0b6', padding: 2 }}><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default NotificationsPage;
